import React, { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { Plus, X, Trash2, Copy, RefreshCw, Pencil, Calculator, MessageCircle, Shirt } from "lucide-react";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";
import PickerWithAdd, { createCustomer, createProduct } from "@/components/PickerWithAdd";
import PricingCalculator from "@/components/PricingCalculator";
import DetailPopup from "@/components/DetailPopup";
import TShirtDiagram, { totalPrintCost } from "@/components/TShirtDiagram";
import AdminDeleteButton from "@/components/AdminDeleteButton";
import WhatsAppButton, { whatsappUrl } from "@/components/WhatsAppButton";
import MobileLineWizard from "@/components/MobileLineWizard";
import FinalReview from "@/components/FinalReview";

const SIZES = ["XS","S","M","L","XL","2XL","3XL","4XL"];
const COMMON_COLORS = ["Black","White","Navy","Grey","Red","Blue","Green","Maroon","Beige","Yellow"];
const CUSTOMIZATION_TYPES = ["","Embroidery","DTF Print","Screen Print","UV Print","Laser Engraving","Plain (no customization)"];
const emptyItem = () => ({ product: "", color: "", qty: 1, unit_price: 0, customization: "", customization_price: 0, other_customization: "", other_customization_cost: 0, other_size: "", estimated_cost: 0, gst_percent: 5, oversize_surcharge: 0, hsn_code: "", category: "", sizes: {}, print_locations: {} });

export default function Quotes() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ customer_id: "", customer_name: "", notes: "", items: [emptyItem()] });
  const nav = useNavigate();

  const load = async () => {
    setItems((await api.get("/quotes")).data);
    setCustomers((await api.get("/customers")).data);
    setProducts((await api.get("/products")).data);
  };
  useEffect(() => { load(); }, []);

  const customerOpts = customers.map(c => ({ value: c.id, label: c.name, meta: c }));
  const productOpts = products.map(p => ({ value: p.name, label: p.name, meta: p }));

  const openNew = () => {
    setEditingId(null);
    setForm({ customer_id: "", customer_name: "", notes: "", items: [emptyItem()] });
    setShowForm(true);
  };
  const openEdit = (q) => {
    setEditingId(q.id);
    setForm({
      customer_id: q.customer_id || "",
      customer_name: q.customer_name,
      notes: q.notes || "",
      items: q.items?.length ? q.items.map(i => ({...i, sizes: i.sizes||{}})) : [emptyItem()],
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const [openCalc, setOpenCalc] = useState(-1);
  const [openTee, setOpenTee] = useState(-1);
  const [popup, setPopup] = useState(null); // quote object shown in modal
  const [customersById, setCustomersById] = useState({});
  useEffect(()=>{ const m={}; customers.forEach(c=>m[c.id]=c); setCustomersById(m); }, [customers]);

  const addRow = () => setForm(f => ({...f, items: [...f.items, emptyItem()]}));
  const rmRow = (i) => setForm(f => ({...f, items: f.items.filter((_,idx)=>idx!==i)}));
  const setRow = (i, k, v) => setForm(f => ({...f, items: f.items.map((it,idx)=> idx===i ? {...it, [k]: v} : it)}));

  const applyProductPrefill = (i, prod) => {
    if (!prod) return;
    setForm(f => ({...f, items: f.items.map((it,idx) => idx===i ? {
      ...it, product: prod.name,
      unit_price: prod.default_price || it.unit_price,
      gst_percent: (prod.default_gst != null ? Number(prod.default_gst) : it.gst_percent),
      hsn_code: prod.hsn_code || it.hsn_code,
      category: prod.category || it.category,
      oversize_surcharge: (prod.oversize_surcharge != null ? Number(prod.oversize_surcharge) : it.oversize_surcharge || 0),
    } : it)}));
  };

  const OVERSIZE_KEYS = ["3XL","4XL","5XL"];
  const oversizeCount = (item) => {
    const s = item.sizes || {};
    return OVERSIZE_KEYS.reduce((a, k) => a + Number(s[k] || 0), 0);
  };

  const totals = (() => {
    const slabs = {};
    let sub = 0;
    form.items.forEach(b => {
      const cust = Number(b.customization_price)||0;
      const other = Number(b.other_customization_cost)||0;
      const over = Number(b.oversize_surcharge)||0;
      const oversizeExtra = over * oversizeCount(b);
      const lineSub = (Number(b.qty)||0)*((Number(b.unit_price)||0) + cust + other) + oversizeExtra;
      const slab = Number(b.gst_percent ?? 5);
      const lineGst = lineSub * slab / 100;
      sub += lineSub;
      if (!slabs[slab]) slabs[slab] = { subtotal: 0, gst: 0, total: 0 };
      slabs[slab].subtotal += lineSub;
      slabs[slab].gst += lineGst;
      slabs[slab].total += lineSub + lineGst;
    });
    const gst = Object.values(slabs).reduce((a,s)=>a+s.gst,0);
    const breakup = Object.entries(slabs).sort(([a],[b])=>Number(a)-Number(b))
      .map(([slab,v]) => ({ slab: Number(slab), ...v }));
    return { sub, gst, total: sub+gst, breakup };
  })();

  const [showReview, setShowReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submitPreview = (e) => {
    e.preventDefault();
    if (!form.customer_name) return toast.error("Pick or add a customer");
    if (!form.items.length || !form.items[0].product) return toast.error("Add at least one line item");
    setShowReview(true);
  };
  const submit = async () => {
    setSubmitting(true);
    try {
      if (editingId) {
        await api.put(`/quotes/${editingId}`, form);
        toast.success("Quote updated");
      } else {
        await api.post("/quotes", form);
        toast.success("Quote created");
      }
      setShowReview(false); setShowForm(false); setEditingId(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSubmitting(false);
  };

  const act = async (id, path) => {
    try { await api.post(`/quotes/${id}/${path}`); load(); toast.success("Done"); }
    catch(e){ toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toOrder = async (id) => {
    try { const { data } = await api.post(`/quotes/${id}/to-order`);
      toast.success(`Order ${data.order_no} created`); nav(`/orders/${data.id}`);
    } catch(e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/q/${token}`);
    toast.success("Public link copied to clipboard");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tiny mb-1">Sales</div>
          <h1 className="font-display text-3xl tracking-tight">Quotes</h1>
          <p className="text-sm text-[#757169] mt-1">Send once, let the customer accept or decline on their own time.</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={()=>{ if(showForm){setShowForm(false);setEditingId(null);} else openNew(); }} data-testid="quotes-add-btn">
          {showForm ? <><X size={16}/> Cancel</> : <><Plus size={16}/> New quote</>}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submitPreview} className="card-surface p-6 space-y-5 fade-up" data-testid="quotes-form">
          <div className="flex items-center justify-between">
            <div className="label-tiny">{editingId ? `Editing quote` : "New quote"}</div>
            {editingId && <span className="badge badge-info mono">{items.find(q=>q.id===editingId)?.quote_no}</span>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <div className="label-tiny mb-1">Customer</div>
              <PickerWithAdd testid="quotes-field-customer"
                requirePhone
                value={form.customer_id}
                onChange={(v, meta) => setForm({...form, customer_id: v, customer_name: meta?.name || "", customer_phone: meta?.phone || form.customer_phone})}
                options={customerOpts}
                placeholder="Choose a customer"
                addLabel="customer"
                onCreate={async (name, phone) => { const o = await createCustomer(name, phone); setCustomers(cs=>[o.meta,...cs]); return o; }}
              />
            </div>
            <div><div className="label-tiny mb-1">Notes to customer</div>
              <input className="input-field" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} placeholder="Optional"/></div>
          </div>
          <p className="text-[10px] text-[#75706A] -mt-3">GST is now product-wise — set on each product&apos;s master record. Line items will auto-pull the correct slab.</p>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-tiny">Line items</div>
              <button type="button" onClick={addRow} className="btn-ghost text-xs flex items-center gap-1"><Plus size={12}/> Add line</button>
            </div>
            <div className="space-y-3">
              {form.items.map((it, i) => (
                <React.Fragment key={i}>
                  {/* MOBILE: stepped wizard */}
                  <div className="md:hidden">
                    <MobileLineWizard
                      index={i}
                      item={it}
                      productOpts={productOpts}
                      oversizeCount={oversizeCount}
                      removable={form.items.length > 1}
                      onRemove={() => rmRow(i)}
                      onProductPick={(meta) => applyProductPrefill(i, meta)}
                      setField={(k, v) => setRow(i, k, v)}
                      setSize={(sz, raw) => {
                        const v = raw === "" ? 0 : Number(raw) || 0;
                        const sizes = { ...(it.sizes || {}) };
                        if (!v) delete sizes[sz]; else sizes[sz] = v;
                        const totalQty = Object.values(sizes).reduce((a, b) => a + Number(b || 0), 0);
                        setForm(f => ({ ...f, items: f.items.map((x, idx) => idx === i ? { ...x, sizes, qty: totalQty || x.qty } : x) }));
                      }}
                      onColorCreate={{
                        forProduct: async (name) => { const o = await createProduct(name); setProducts(ps => [o.meta, ...ps]); return o; },
                        forColor: async (name) => ({ value: name, label: name }),
                      }}
                    />
                  </div>
                  {/* DESKTOP: inline flat row */}
                  <div className="hidden md:block border border-[#EAE5DB] rounded-md p-3 bg-[#FBF8F1]">
                  <div className="grid grid-cols-2 md:grid-cols-12 gap-2 items-end">
                    <div className="col-span-2 md:col-span-4">
                      <div className="label-tiny mb-1">Product</div>
                      <PickerWithAdd testid={`quote-item-product-${i}`}
                        value={it.product}
                        onChange={(v, meta) => { setRow(i,"product",v); if (meta) applyProductPrefill(i, meta); }}
                        options={productOpts}
                        placeholder="Choose product"
                        addLabel="product"
                        onCreate={async (name)=>{ const o = await createProduct(name); setProducts(ps=>[o.meta,...ps]); return o; }}
                      />
                    </div>
                    <div className="col-span-1 md:col-span-2">
                      <div className="label-tiny mb-1">Color</div>
                      <PickerWithAdd testid={`quote-item-color-${i}`}
                        value={it.color}
                        onChange={v=>setRow(i,"color",v)}
                        options={COMMON_COLORS.map(c=>({value:c,label:c}))}
                        placeholder="Color"
                        addLabel="color"
                        onCreate={async (name)=> ({ value: name, label: name })}
                      />
                    </div>
                    <div className="col-span-1 md:col-span-2">
                      <div className="label-tiny mb-1">Customization</div>
                      <select className="input-field" value={it.customization} onChange={e=>setRow(i,"customization",e.target.value)}>
                        {CUSTOMIZATION_TYPES.map(c=><option key={c} value={c}>{c || "—"}</option>)}
                      </select>
                    </div>
                    <div className="col-span-1 md:col-span-1">
                      <div className="label-tiny mb-1">Qty</div>
                      <input type="number" className="input-field" value={it.qty || ""} onChange={e=>setRow(i,"qty",Number(e.target.value)||0)} data-testid={`quote-item-qty-${i}`}/>
                    </div>
                    <div className="col-span-1 md:col-span-2">
                      <div className="label-tiny mb-1">Unit ₹</div>
                      <div className="flex gap-1">
                        <input type="number" className="input-field" value={it.unit_price || ""} onChange={e=>setRow(i,"unit_price",Number(e.target.value)||0)} data-testid={`quote-item-price-${i}`}/>
                        <button type="button" onClick={()=>setOpenCalc(openCalc===i?-1:i)} className="btn-ghost text-xs px-2" title="Customization calculator" data-testid={`calc-open-${i}`}><Calculator size={13}/></button>
                        <button type="button" onClick={()=>setOpenTee(openTee===i?-1:i)} className="btn-ghost text-xs px-2" title="Print locations diagram" data-testid={`tee-open-${i}`}><Shirt size={13}/></button>
                      </div>
                    </div>
                    <div className="col-span-2 md:col-span-1 flex justify-end">
                      {form.items.length > 1 && <button type="button" onClick={()=>rmRow(i)} className="p-2 text-[#B83A3A] hover:bg-[#F7DFDF] rounded"><Trash2 size={14}/></button>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-10 gap-2 mt-2">
                    <div className="md:col-span-2">
                      <div className="label-tiny mb-1">GST %</div>
                      <input type="number" step="1" className="input-field" value={it.gst_percent ?? 18}
                        onChange={e=>setRow(i,"gst_percent",Number(e.target.value)||0)} data-testid={`quote-item-gst-${i}`}/>
                    </div>
                    <div className="md:col-span-2">
                      <div className="label-tiny mb-1">Cust ₹/pc</div>
                      <input type="number" className="input-field" value={it.customization_price ?? 0}
                        onChange={e=>setRow(i,"customization_price",Number(e.target.value)||0)} data-testid={`quote-item-custprice-${i}`}/>
                    </div>
                    <div className="md:col-span-2">
                      <div className="label-tiny mb-1">3XL+ ₹/pc</div>
                      <input type="number" className="input-field" value={it.oversize_surcharge ?? 0}
                        onChange={e=>setRow(i,"oversize_surcharge",Number(e.target.value)||0)} data-testid={`quote-item-oversize-${i}`}/>
                    </div>
                    <div className="md:col-span-2">
                      <div className="label-tiny mb-1">HSN</div>
                      <input className="input-field" value={it.hsn_code || ""} onChange={e=>setRow(i,"hsn_code",e.target.value)}/>
                    </div>
                    <div className="col-span-2 md:col-span-2">
                      <div className="label-tiny mb-1">Line total</div>
                      <div className="input-field bg-[#F6F1E7] mono">{inr((Number(it.qty)||0)*((Number(it.unit_price)||0) + (Number(it.customization_price)||0)) + (Number(it.oversize_surcharge)||0) * oversizeCount(it))}</div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="label-tiny mb-1.5">Size grid <span className="normal-case text-[10px]">(auto-sums into Qty)</span></div>
                    <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
                      {SIZES.map(sz => (
                        <div key={sz}>
                          <div className="text-[10px] text-center text-[#757169] mb-0.5 font-medium">{sz}</div>
                          <input type="number" min="0" className="input-field text-center px-1"
                            value={it.sizes?.[sz] ?? ""} placeholder="—"
                            data-testid={`quote-item-size-${i}-${sz}`}
                            onChange={e=>{
                              const v = e.target.value === "" ? "" : Number(e.target.value);
                              const sizes = {...(it.sizes||{})};
                              if (v === "" || v === 0) delete sizes[sz]; else sizes[sz] = v;
                              const total = Object.values(sizes).reduce((a,b)=>a+Number(b||0),0);
                              setForm(f => ({...f, items: f.items.map((x,idx)=> idx===i ? {...x, sizes, qty: total || x.qty} : x)}));
                            }} />
                        </div>
                      ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                      <div>
                        <div className="label-tiny mb-1">Other size (free-text)</div>
                        <input className="input-field" value={it.other_size || ""} placeholder='e.g. "kids XS", "26w x 32l"'
                          onChange={e=>setRow(i,"other_size",e.target.value)} data-testid={`quote-item-other-size-${i}`}/>
                      </div>
                      <div>
                        <div className="label-tiny mb-1">Other customization (free-text)</div>
                        <input className="input-field" value={it.other_customization || ""} placeholder='e.g. "reflective tape on hem", "sublimation panel"'
                          onChange={e=>setRow(i,"other_customization",e.target.value)} data-testid={`quote-item-other-cust-${i}`}/>
                      </div>
                    </div>
                  </div>
                  {openTee === i && (
                    <div className="mt-3 p-3 bg-white border border-[#EAE5DB] rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <div className="label-tiny">Print / embroidery locations & pricing</div>
                        <button type="button" className="text-xs text-[#75706A] hover:text-[#211F1D]" onClick={()=>setOpenTee(-1)}>Close</button>
                      </div>
                      <TShirtDiagram
                        value={it.print_locations || {}}
                        onChange={(v)=>setRow(i, "print_locations", v)}
                        onCost={(cost) => {
                          // Store customization cost per piece separately so users see it in the "Customization ₹/pc" field.
                          setForm(f => ({...f, items: f.items.map((x, idx) => idx === i
                            ? { ...x, estimated_cost: cost, customization_price: Math.round(cost * 100) / 100 }
                            : x)}));
                        }}
                      />
                    </div>
                  )}
                  {openCalc === i && (
                    <div className="mt-3">
                      <PricingCalculator
                        initialMethod={(it.customization||"").toLowerCase().includes("embroid") ? "embroidery"
                          : (it.customization||"").toLowerCase().includes("screen") ? "screen" : "dtf"}
                        onChange={(cost) => {
                          setForm(f => ({...f, items: f.items.map((x,idx)=> idx===i ? {...x, estimated_cost: cost, customization_price: Math.round(cost*100)/100} : x)}));
                        }}
                        onClose={()=>setOpenCalc(-1)}
                      />
                      <div className="text-[10px] text-[#757169] mt-1">Tip: the calculator fills the <b>Customization ₹/pc</b> field. You can override that number manually — it always adds on top of the base Unit ₹.</div>
                    </div>
                  )}
                  {Object.keys(it.print_locations||{}).length > 0 && openTee !== i && (
                    <div className="mt-2 text-xs text-[#75706A] flex flex-wrap gap-1.5 items-center">
                      <span className="label-tiny">Print at:</span>
                      {Object.keys(it.print_locations).map(k => (
                        <span key={k} className="badge badge-info">{k.replace(/_/g," ")}</span>
                      ))}
                      <span className="ml-2 text-[10px]">· print cost ₹{totalPrintCost(it.print_locations).total.toFixed(0)}/pc</span>
                    </div>
                  )}
                  </div>{/* /desktop */}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="flex justify-between items-start pt-4 border-t border-[#EAE5DB] flex-wrap gap-4">
            <div className="text-sm text-[#757169] space-y-0.5">
              <div>Subtotal: <span className="mono text-[#2C2B29]">{inr(totals.sub)}</span></div>
              {totals.breakup.map(b => (
                <div key={b.slab}>GST @ {b.slab}% on {inr(b.subtotal)}: <span className="mono text-[#2C2B29]">{inr(b.gst)}</span></div>
              ))}
              <div className="font-display text-lg font-bold text-[#2C2B29]">Total: {inr(totals.total)}</div>
            </div>
            <button type="submit" className="btn-primary" data-testid="quotes-submit">{editingId ? "Save changes → Review" : "Review & create quote"}</button>
          </div>
        </form>
      )}

      <FinalReview
        open={showReview}
        onClose={() => setShowReview(false)}
        onConfirm={submit}
        title={editingId ? "Save changes to quote?" : "Create quote?"}
        submittingLabel={editingId ? "Save changes" : "Create quote"}
        submitting={submitting}
        customerName={form.customer_name}
        customerPhone={form.customer_phone}
        items={form.items}
        totals={totals}
      />

      <div className="card-surface overflow-x-auto">
        <table className="data-table">
          <thead><tr>
            <th>Quote #</th><th>Customer</th><th>Items</th><th>Total</th><th>Status</th><th>Actions</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#757169]">No quotes yet.</td></tr>}
            {items.map(q => {
              const cust = q.customer_id ? customersById[q.customer_id] : null;
              const phone = cust?.phone || "";
              const link = `${window.location.origin}/q/${q.token}`;
              const waMsg = `Hi ${q.customer_name}, here's your quote ${q.quote_no} from Marsh Clothing — total ₹${Number(q.total).toLocaleString("en-IN")}. Tap to accept or decline: ${link}`;
              return (
              <tr key={q.id} data-testid={`quote-row-${q.id}`} className="cursor-pointer" onClick={()=>setPopup(q)}>
                <td className="mono">{q.quote_no}</td>
                <td>{q.customer_name}</td>
                <td>{q.items?.length || 0}</td>
                <td className="mono">{inr(q.total)}</td>
                <td>
                  <span className={`badge ${q.status==="accepted"?"badge-success":q.status==="declined"?"badge-danger":q.status==="sent"?"badge-info":"badge-neutral"}`}>
                    {q.status}
                  </span>
                </td>
                <td onClick={e=>e.stopPropagation()}>
                  <div className="flex flex-wrap gap-1.5">
                    {q.status !== "accepted" && q.status !== "declined" && (
                      <button className="btn-ghost text-xs flex items-center gap-1" onClick={()=>openEdit(q)} data-testid={`quote-edit-${q.id}`}><Pencil size={11}/>Edit</button>
                    )}
                    {q.status !== "accepted" && q.status !== "declined" && (
                      <>
                        <button className="btn-ghost text-xs" onClick={()=>act(q.id,"accept")} data-testid={`quote-accept-${q.id}`}>Accept</button>
                        <button className="btn-ghost text-xs" onClick={()=>act(q.id,"decline")}>Decline</button>
                      </>
                    )}
                    {q.status === "accepted" && !q.order_id && (
                      <button className="btn-primary text-xs" onClick={()=>toOrder(q.id)} data-testid={`quote-to-order-${q.id}`}>→ Order</button>
                    )}
                    {q.order_id && (
                      <Link to={`/orders/${q.order_id}`} className="btn-primary text-xs inline-flex items-center gap-1" data-testid={`quote-open-order-${q.id}`}>
                        Open order →
                      </Link>
                    )}
                    {phone
                      ? <a href={whatsappUrl(phone, waMsg)} target="_blank" rel="noreferrer"
                          className="px-2.5 py-1 text-xs rounded-full bg-[#25D366] text-white hover:bg-[#1EBE5A] inline-flex items-center gap-1"
                          style={{fontFamily:"Manrope, sans-serif"}} data-testid={`quote-wa-${q.id}`}><MessageCircle size={11}/>Send</a>
                      : <button className="btn-ghost text-xs flex items-center gap-1" onClick={()=>copyLink(q.token)}><Copy size={11}/>Link</button>}
                    <a href={`/print/quote/${q.id}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs">Print</a>
                    <AdminDeleteButton endpoint={`/quotes/${q.id}`} label={`quote ${q.quote_no}`} onDone={load} testid={`delete-quote-${q.id}`} />
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <DetailPopup open={!!popup} onClose={()=>setPopup(null)}
        subtitle="Quote" title={popup?.quote_no || ""}
        actions={popup && <>
          {popup.order_id && (
            <Link to={`/orders/${popup.order_id}`} className="btn-primary text-xs inline-flex items-center gap-1" data-testid={`popup-open-order-${popup.id}`}>Open converted order →</Link>
          )}
          <a href={`/q/${popup.token}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs">Open public link</a>
          <a href={`/print/quote/${popup.id}`} target="_blank" rel="noreferrer" className="btn-primary text-xs">Print</a>
        </>}>
        {popup && <>
          <div><span className="text-[#75706A]">Customer:</span> <b>{popup.customer_name}</b></div>
          <div><span className="text-[#75706A]">Status:</span> <span className={`badge ${popup.status==="accepted"?"badge-success":popup.status==="declined"?"badge-danger":"badge-neutral"}`}>{popup.status}</span></div>
          <div><span className="text-[#75706A]">Items:</span> {popup.items?.length}</div>
          <div><span className="text-[#75706A]">Subtotal:</span> <span className="mono">{inr(popup.subtotal)}</span> · GST {popup.gst_percent}% <span className="mono">{inr(popup.gst_amount)}</span></div>
          <div className="font-display font-bold text-lg mt-1">Total {inr(popup.total)}</div>
          <div className="mt-3 border-t border-[#E7E1D3] pt-3">
            <div className="label-tiny mb-1">Line items</div>
            {popup.items?.map((it,i)=> <div key={i} className="text-xs py-1 flex justify-between"><span>{it.product}{it.color && ` · ${it.color}`}{it.customization && ` · ${it.customization}`}</span><span className="mono">{it.qty} × {inr(it.unit_price)}</span></div>)}
          </div>
        </>}
      </DetailPopup>
    </div>
  );
}
